import Skeleton, { SkeletonRows } from "./Skeleton";

/**
 * The one loading placeholder for the whole app.
 *
 * There were 624 loading branches across the codebase and between them a single
 * use of the skeleton class — everything else rendered the word "Loading…" in
 * grey, centred in 40px of padding. A skeleton that matches the shape of what
 * is about to appear is what makes a screen feel like it is filling in rather
 * than stalling, so pick the variant that matches the surrounding layout:
 *
 *   <LoadingState />                  a short block — the safe default
 *   <LoadingState variant="table" />  header + rows, for a data table
 *   <LoadingState variant="list" />   avatar + two lines per row
 *   <LoadingState variant="cards" />  a grid of card placeholders
 *   <LoadingState variant="text" />   a paragraph of lines
 *   <LoadingState variant="inline" /> one line, for a small widget
 *
 * `label` is not rendered — it names the region for assistive tech, which
 * otherwise announces nothing at all while a screen loads.
 */
export default function LoadingState({
  variant = "block",
  rows = 5,
  cards = 4,
  label = "Loading",
  style,
}) {
  const wrap = (children, pad = 20) => (
    <div role="status" aria-live="polite" aria-busy="true" style={{ padding: pad, ...(style || {}) }}>
      <span className="sr-only">{label}</span>
      {children}
    </div>
  );

  if (variant === "inline") {
    return wrap(<Skeleton w="60%" h={12} />, 8);
  }

  if (variant === "text") {
    return wrap(
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <Skeleton w="92%" h={12} />
        <Skeleton w="98%" h={12} />
        <Skeleton w="74%" h={12} />
      </div>
    );
  }

  if (variant === "list") {
    return wrap(<SkeletonRows count={rows} />);
  }

  if (variant === "cards") {
    return wrap(
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 16 }}>
        {Array.from({ length: cards }).map((_, i) => (
          <div key={i} className="card" style={{ padding: 18 }}>
            <Skeleton w="52%" h={11} style={{ marginBottom: 14 }} />
            <Skeleton w="78%" h={20} style={{ marginBottom: 10 }} />
            <Skeleton w="40%" h={10} />
          </div>
        ))}
      </div>
    );
  }

  if (variant === "table") {
    // Column widths vary so the placeholder reads as a table rather than a
    // stack of identical bars.
    const cols = [26, 20, 16, 14, 12];
    return wrap(
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", gap: 16 }}>
          {cols.map((w, i) => <Skeleton key={i} w={`${w}%`} h={10} />)}
        </div>
        {Array.from({ length: rows }).map((_, r) => (
          <div key={r} style={{ display: "flex", gap: 16, alignItems: "center" }}>
            {cols.map((w, i) => <Skeleton key={i} w={`${w}%`} h={13} />)}
          </div>
        ))}
      </div>
    );
  }

  return wrap(
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <Skeleton w="45%" h={14} />
      <Skeleton w="100%" h={12} />
      <Skeleton w="82%" h={12} />
    </div>
  );
}
